import { beginCartMutation, blockCartCheckout } from './cart-state.js';
import { DISCOUNT_ORIGIN_ATTRIBUTE, readDiscountOrigin, discountOriginAttributes, resolveDiscountOrigin } from '@theme/discount-origin';
import {
  buildDiscountCodeList,
  getPrimaryDiscountCodeFromUrl,
  isAdditiveFreeProcessingDiscountCode,
} from '@theme/free-processing-discount';
import { isDiscountQuoteStale, createDiscountQuoteRefresher, lookupDiscountMetadata } from './discount-pricing.js';

const normalizeCode = (code) =>
  typeof code === 'string' && code.trim() ? code.trim().toUpperCase() : null;

const clearDiscountUrl = () => {
  const url = new URL(window.location.href);
  url.searchParams.delete('lb_discount');
  url.searchParams.delete('lb_discount_type');
  url.searchParams.delete('discount');
  window.history.replaceState({}, '', url);
};

/**
 * Applies discounts before the cart contains items. The metadata worker is required here because
 * Shopify may report an otherwise valid product/order code as not applicable until qualifying
 * lines exist.
 */
export async function bootstrapEmptyCartDiscount() {
  if (document.querySelector('cart-discount-component')) return;

  const endMutation = beginCartMutation();
  const config = Theme.legacyBox.discounts;
  let initialSession = sessionStorage.getItem(config.sessionKey);
  // Additive-offer capture removes its own URL parameter. Only a changed primary
  // selection (or page) should invalidate this initialization.
  const selectionUrlKey = () => {
    const url = new URL(window.location.href);
    return JSON.stringify([url.origin, url.pathname, ...['discount', 'lb_discount', 'lb_discount_type']
      .map((key) => url.searchParams.get(key))]);
  };
  const initialUrl = selectionUrlKey();
  const stillOwnsSelection = () =>
    sessionStorage.getItem(config.sessionKey) === initialSession &&
    selectionUrlKey() === initialUrl &&
    !document.querySelector('cart-discount-component');
  const clearDefault = () => sessionStorage.removeItem(config.defaultSessionKey);
  const readStored = () => {
    const value = sessionStorage.getItem(config.sessionKey);
    if (!value) return null;

    try {
      return JSON.parse(value);
    } catch {
      sessionStorage.removeItem(config.sessionKey);
      initialSession = null;
      return null;
    }
  };
  const resolveAutoCode = async (fallbackCode, triggerAssignment = false) => {
    if (!config.autoApply.active || !fallbackCode) return null;

    try {
      return (
        (await window.Legacybox?.resolveSitewideAutoApplyDiscountCode?.({
          fallbackCode,
          triggerAssignment,
        })) || fallbackCode
      );
    } catch (error) {
      console.error(error);
      return fallbackCode;
    }
  };
  const applyDiscount = async (code, appliedType = 'auto_apply') => {
    if (!code || !stillOwnsSelection()) return false;

    let discountData = { code: normalizeCode(code) };
    let hasWorkerData = false;

    try {
      discountData = await lookupDiscountMetadata(config, code) || discountData;
      hasWorkerData = discountData.status === 'success';
    } catch (error) {
      console.error(error);
    }

    if (!stillOwnsSelection() || discountData.status === 'fail') return false;

    const response = await fetch(Theme.routes.cart_update_url, {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      method: 'POST',
      body: JSON.stringify({
        discount: buildDiscountCodeList(code).join(','),
        attributes: isAdditiveFreeProcessingDiscountCode(code) ? {} : discountOriginAttributes(
          code, appliedType, config.autoApply.originalCode || config.autoApply.code
        ),
      }),
    });
    if (!response.ok) throw new Error(`Discount update failed (${response.status})`);

    const data = await response.json();
    if (!stillOwnsSelection()) return false;
    if (isAdditiveFreeProcessingDiscountCode(code)) {
      clearDefault();
    } else {
      const sessionData = JSON.stringify({
        applied: appliedType,
        ...discountData,
        ...(window.Legacybox?.buildSitewideDiscountAbSessionData?.(code, appliedType) || {}),
      });

      sessionStorage.setItem(config.sessionKey, sessionData);
      if (appliedType === 'auto_apply' && hasWorkerData) {
        sessionStorage.setItem(config.defaultSessionKey, sessionData);
      } else {
        clearDefault();
      }
    }

    clearDiscountUrl();
    document.dispatchEvent(new CustomEvent('discount:update', { detail: { data } }));
    return true;
  };

  try {
    const params = new URLSearchParams(window.location.search);
    const urlCode = getPrimaryDiscountCodeFromUrl();
    const requestedType = params.get('lb_discount_type') || 'url_query';
    const requestedCode = normalizeCode(urlCode);
    let storedDiscount = readStored();
    const rawCartOrigin = Theme.legacyBox.cart?.attributes?.[DISCOUNT_ORIGIN_ATTRIBUTE];
    const cartOrigin = readDiscountOrigin(rawCartOrigin);
    const needsOrigin = (code, applied) => !initialSession || rawCartOrigin !== discountOriginAttributes(
      code, applied, config.autoApply.originalCode || config.autoApply.code
    )[DISCOUNT_ORIGIN_ATTRIBUTE];
    if (!storedDiscount && cartOrigin) storedDiscount = { code: cartOrigin.code, applied: cartOrigin.applied };
    if (storedDiscount) storedDiscount = { ...storedDiscount, applied: resolveDiscountOrigin(
      storedDiscount.code, storedDiscount, cartOrigin, config.autoApply.previousCodes
    ) };

    if (isAdditiveFreeProcessingDiscountCode(storedDiscount?.code)) {
      sessionStorage.removeItem(config.sessionKey);
      storedDiscount = null;
      initialSession = null;
    }

    if (storedDiscount) {
      const storedCode = normalizeCode(storedDiscount.code);

      if (requestedCode) {
        if (storedCode === requestedCode) {
          if (needsOrigin(storedCode, requestedType) || storedDiscount.applied !== requestedType || buildDiscountCodeList(storedCode).length > 1 || isDiscountQuoteStale(storedDiscount)) {
            if (!(await applyDiscount(storedCode, requestedType))) return;
          }
          clearDefault();
          clearDiscountUrl();
          return;
        }
        await applyDiscount(urlCode, requestedType);
        return;
      }

      if (storedDiscount.applied !== 'auto_apply') {
        if (needsOrigin(storedCode, storedDiscount.applied) || buildDiscountCodeList(storedCode).length > 1 || isDiscountQuoteStale(storedDiscount)) {
          await applyDiscount(storedCode, storedDiscount.applied);
        }
        clearDefault();
        return;
      }

      const snapshot = sessionStorage.getItem(config.sessionKey);
      const autoCode = await resolveAutoCode(config.autoApply.originalCode || config.autoApply.code);
      if (sessionStorage.getItem(config.sessionKey) !== snapshot) return;
      const defaultCode = normalizeCode(autoCode);
      if (storedCode === defaultCode) {
        if (needsOrigin(storedCode, storedDiscount.applied) || buildDiscountCodeList(storedCode).length > 1 || isDiscountQuoteStale(storedDiscount)) {
          await applyDiscount(storedCode, 'auto_apply');
        } else {
          sessionStorage.setItem(config.defaultSessionKey, JSON.stringify(storedDiscount));
        }
        return;
      }

      if (autoCode) await applyDiscount(autoCode, 'auto_apply');
      return;
    }

    const autoCode = requestedCode ? null : await resolveAutoCode(config.autoApply.code, true);
    if (!stillOwnsSelection()) return;
    const campaignCode = buildDiscountCodeList(null)[0] || null;
    const code = urlCode || autoCode || campaignCode;
    const type = requestedCode ? requestedType : autoCode ? 'auto_apply' : 'campaign';

    if (!(await applyDiscount(code, type)) && campaignCode && code !== campaignCode) {
      await applyDiscount(campaignCode, 'campaign');
    }
  } catch (error) {
    console.error(error);
    blockCartCheckout();
  } finally {
    endMutation();
    document.body.classList.remove('discounts-loading');
    updateOfferStatus();
  }
}

/** Operational status, separate from prices and merchant-authored campaign copy. */
function updateOfferStatus() {
  const config = Theme.legacyBox.discounts;
  const read = key => { try { return JSON.parse(sessionStorage.getItem(key)); } catch { return null; } };
  const applied = read(config.sessionKey);
  const data = applied?.applied === 'override_applied' && config.autoApply?.active
    ? read(config.defaultSessionKey) : applied;
  const checking = config.allow && document.body.classList.contains('discounts-loading');
  const unavailable = config.allow && data?.code && (!data.status || isDiscountQuoteStale(data));
  for (const status of document.querySelectorAll('[data-discount-status]')) {
    status.hidden = !checking && !unavailable;
    status.textContent = checking ? Theme.translations.checking_offer : unavailable ? Theme.translations.offer_unavailable : '';
  }
}

document.addEventListener('discount:update', updateOfferStatus);
document.addEventListener('shopify:section:load', updateOfferStatus);
document.addEventListener('DOMContentLoaded', async () => {
  updateOfferStatus();
  if (typeof MutationObserver !== 'undefined') {
    new MutationObserver(updateOfferStatus).observe(document.body, { attributes: true, attributeFilter: ['class'] });
  }
  if (Theme.legacyBox.cart?.item_count === 0) await bootstrapEmptyCartDiscount();
  const refresh = createDiscountQuoteRefresher({
    config: Theme.legacyBox.discounts, storage: sessionStorage, fetcher: fetch, lookup: lookupDiscountMetadata,
    notify: () => document.dispatchEvent(new CustomEvent('discount:update', { detail: { data: {}, cartChanged: false } })),
    isBusy: () => document.visibilityState === 'hidden' ||
      document.body.classList.contains('discounts-loading') ||
      document.body.classList.contains('discounts-override-applying') ||
      Boolean(document.querySelector('.cart-discount.is-loading')) ||
      Array.from(document.querySelectorAll('cart-discount-component')).some(component => component.isUpdatingDiscount),
  });
  document.addEventListener('legacybox:discount-quote-expired', refresh);
  document.addEventListener('visibilitychange', refresh);
  // Retry transient failures and migrate app records written by the older worker.
  setInterval(refresh, 15000);
  refresh();
});
